import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listAPI, guessAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";

const ListDetail = () => {
  const { slug } = useParams();
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [guess, setGuess] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("alphabetical");

  // Set default sort to "rank" for ranked lists
  useEffect(() => {
    if (list && list.isRanked) {
      setSortOrder("rank");
    }
  }, [list]);
  const [difficultyMode, setDifficultyMode] = useState(() => {
    const saved = localStorage.getItem(`difficulty_${slug}`);
    return saved || "casual";
  });
  const [showDifficultyInfo, setShowDifficultyInfo] = useState(false);

  usePageTitle(list?.title);

  useEffect(() => {
    localStorage.setItem(`difficulty_${slug}`, difficultyMode);
  }, [difficultyMode, slug]);

  useEffect(() => {
    loadList();
  }, [slug]);

  const loadList = async () => {
    setLoading(true);
    try {
      const response = await listAPI.getListDetailBySlug(slug);
      setList(response.data);
    } catch (error) {
      console.error("Failed to load list:", error);
      setMessage("Failed to load list");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleGuess = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setMessage("You must be logged in to guess!");
      setMessageType("error");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    if (!guess.trim()) return;

    try {
      const response = await guessAPI.submitGuess(
        list.id,
        guess.trim(),
        difficultyMode
      );

      if (response.data.isCorrect) {
        setMessageType("success");
        setMessage(response.data.message || "Correct!");
        setGuess("");
        loadList();
      } else {
        setMessageType("error");
        setMessage(response.data.message || "Incorrect guess. Try again!");
      }
    } catch (error) {
      setMessageType("error");
      setMessage("Failed to submit guess");
    }

    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 5000);
  };

  const handleReset = async () => {
    if (
      window.confirm(
        "Are you sure you want to reset your progress on this list?"
      )
    ) {
      try {
        await guessAPI.resetProgress(list.id);
        loadList();
        setMessage("Progress reset successfully!");
        setMessageType("success");
      } catch (error) {
        setMessage("Failed to reset progress");
        setMessageType("error");
      }
    }
  };

  const getSortedItems = () => {
    if (!list || !list.guessedItems) return [];

    let items = [...list.guessedItems];

    switch (sortOrder) {
      case "alphabetical":
        return items.sort((a, b) => a.answer.localeCompare(b.answer));
      case "reverse":
        return items.sort((a, b) => b.answer.localeCompare(a.answer));
      case "rank":
        return items.sort(
          (a, b) => (a.rankPosition || 0) - (b.rankPosition || 0)
        );
      default:
        return items;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-lg sm:text-xl text-white">Loading...</div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-xl sm:text-2xl text-red-500 mb-4">
            List not found
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-blue-400 hover:text-blue-300 underline"
          >
            Go back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4 leading-tight">
          {list.title}
        </h1>

        {list.description && (
          <div className="text-gray-400 mb-4 prose prose-invert prose-sm max-w-none text-sm sm:text-base">
            <ReactMarkdown
              components={{
                p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                strong: ({ node, ...props }) => (
                  <strong className="text-white font-semibold" {...props} />
                ),
                em: ({ node, ...props }) => (
                  <em className="text-gray-300" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc list-inside mb-2 space-y-1"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li className="text-gray-400" {...props} />
                ),
              }}
            >
              {list.description}
            </ReactMarkdown>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4">
          {list.categories
            .filter((cat) => cat.name.toLowerCase() !== "ranked")
            .map((cat) => (
              <span
                key={cat.id}
                className="px-2 sm:px-3 py-1 bg-blue-600/20 text-blue-400 text-xs sm:text-sm rounded whitespace-nowrap"
              >
                {cat.name}
              </span>
            ))}
          {list.isRanked && (
            <span className="px-2 sm:px-3 py-1 bg-yellow-600/20 text-yellow-400 text-xs sm:text-sm rounded">
              Ranked
            </span>
          )}
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-xs sm:text-sm text-gray-400 mb-2">
            <span>
              Progress: {list.guessedCount} / {list.totalItems}
            </span>
            <span className="font-medium">
              {Math.round((list.guessedCount / list.totalItems) * 100)}%
            </span>
          </div>
          <div className="w-full bg-dark-bg rounded-full h-2.5 sm:h-3 border border-gray-600">
            <div
              className="bg-blue-600 h-full rounded-full transition-all duration-300"
              style={{
                width: `${(list.guessedCount / list.totalItems) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        {list.isCompleted && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded mb-4 text-sm sm:text-base">
            🎉 Congratulations! You've completed this list!
          </div>
        )}

        {!list.isCompleted && isAuthenticated && (
          <div className="bg-dark-card border border-gray-700 rounded-lg p-3 sm:p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <span className="text-gray-400 text-xs sm:text-sm font-medium">
                  Difficulty:
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDifficultyMode("casual")}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition ${
                      difficultyMode === "casual"
                        ? "bg-blue-600 text-white"
                        : "bg-dark-bg text-gray-400 hover:text-white border border-gray-600"
                    }`}
                  >
                    Casual
                  </button>
                  <button
                    onClick={() => setDifficultyMode("relaxed")}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition ${
                      difficultyMode === "relaxed"
                        ? "bg-green-600 text-white"
                        : "bg-dark-bg text-gray-400 hover:text-white border border-gray-600"
                    }`}
                  >
                    Relaxed
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowDifficultyInfo(!showDifficultyInfo)}
                className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 transition self-end sm:self-auto"
              >
                <span>{showDifficultyInfo ? "Hide Info" : "What's this?"}</span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    showDifficultyInfo ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            {showDifficultyInfo && (
              <div className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-400 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-blue-400 font-semibold sm:min-w-[70px]">
                    Casual:
                  </span>
                  <span>
                    Exact match, ignores accents.{" "}
                    <span className="text-gray-500 block sm:inline">
                      "Beyonce" = "Beyoncé"
                    </span>
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-green-400 font-semibold sm:min-w-[70px]">
                    Relaxed:
                  </span>
                  <span>
                    90% similarity, typos OK.{" "}
                    <span className="text-gray-500 block sm:inline">
                      "Swiming" = "Swimming"
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!list.isCompleted && isAuthenticated && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
          <form onSubmit={handleGuess}>
            <label className="block text-gray-300 mb-2 font-semibold text-sm sm:text-base">
              Enter your guess:
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Type your answer..."
                className="flex-1 px-4 py-3 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500 transition"
                autoFocus
              />
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition whitespace-nowrap text-sm sm:text-base"
              >
                Guess
              </button>
            </div>
          </form>

          {message && (
            <div
              className={`mt-4 px-4 py-3 rounded text-sm sm:text-base ${
                messageType === "success"
                  ? "bg-green-500/10 border border-green-500 text-green-500"
                  : "bg-red-500/10 border border-red-500 text-red-500"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      )}

      {!isAuthenticated && (
        <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded mb-6 text-sm sm:text-base">
          You must be{" "}
          <a href="/login" className="underline font-semibold">
            logged in
          </a>{" "}
          to guess items.
        </div>
      )}

      <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {isAdmin ? "All Items" : "Your Guessed Items"}
          </h2>

          {list.isRanked && list.guessedItems.length > 0 && (
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
            >
              <option value="alphabetical">A → Z</option>
              <option value="reverse">Z → A</option>
              <option value="rank">By Rank</option>
            </select>
          )}
        </div>

        {list.guessedCount === 0 ? (
          <p className="text-gray-400 text-center py-8 text-sm sm:text-base">
            No items guessed yet. Start guessing above!
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {getSortedItems().map((item) => (
              <div
                key={item.id}
                className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg flex justify-between items-center gap-2"
              >
                <span className="text-white font-medium text-sm sm:text-base truncate">
                  {item.answer}
                </span>
                {list.isRanked && item.rankPosition && (
                  <span className="text-gray-400 text-xs sm:text-sm flex-shrink-0">
                    #{item.rankPosition}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isAuthenticated && list.guessedCount > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={handleReset}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg transition text-sm sm:text-base font-medium"
          >
            Reset Progress
          </button>
        </div>
      )}
    </div>
  );
};

export default ListDetail;
